import { MOVEMENT_TRAVEL_CONFIG } from '../../../config/abilities/movementTravel.config';
import { TRAIL_FX_CONFIG } from '../../../config/abilities/trailFx.config';
import { ABILITY_DATA } from '../../../shared/content/generated/abilities.gen';
import { playerCombatPoint } from '../../../shared/logic/CombatAnchors';
import { resolveCastGeometry, type DashGeometry } from '../castGeometry/castGeometry';
import { REMOTE_VFX_CONFIG } from '../config/remote-vfx.config';
import { TrailSystem } from '../players/TrailSystem';

/** Safety bounds on presentation only. No position claim or movement allowance. */
export const REMOTE_DASH_MAX_MS = 15000;
const HYDRATION_GRACE_MS = 1000;
const LANDING_POSE_EPSILON_PX = 6;
const LAUNCH_OBSERVATION_MS = 100;
const MAX_SEQUENCE = 0x7fffffff;
const MAX_SEQUENCE_HISTORY = 128;

export interface RemoteDashReceipt {
  sessionId: string; x: number; y: number; abilityId?: string; travelMs: number; clientDashSeq?: number;
}
export interface RemoteDashPose {
  position: { x: number; y: number }; width?: number; height?: number; facing?: string;
  health?: number; isDead?: boolean; isOnline?: boolean;
  isGrounded?: boolean; velocity?: { x: number; y: number };
  /** Latest accepted position; the rendered position above may lag behind it. */
  authority?: { x: number; y: number; isGrounded: boolean; velocityY: number };
}
export interface RemoteDashVFXDeps {
  caster: (id: string) => RemoteDashPose | null;
  textureKey: (id: string) => string | null;
  spawn: (data: { effectId: string; x: number; y: number; abilityId?: string; [key: string]: unknown }) => void;
  instant: (data: RemoteDashReceipt) => void;
  arrival: (data: RemoteDashReceipt) => void;
  now?: () => number;
}
interface Run {
  receipt: RemoteDashReceipt; geometry?: DashGeometry; startedAt: number;
  trailId?: string; landingId?: string; carries: boolean; waitingForGroundContact?: boolean;
  seenCaster: boolean; spawnedOrigin: boolean; airborne: boolean; interval: ReturnType<typeof setInterval>;
  timeout: ReturnType<typeof setTimeout>;
}

/** Owns remote dash presentation. Timed abilities retain their arrival plan;
 * an authored airborne carry ends only at a witnessed rendered landing. */
export class RemoteDashVFX {
  private readonly runs = new Map<string, Run>();
  private readonly sequences = new Map<string, number>();
  constructor(private readonly deps: RemoteDashVFXDeps) {}

  start(receipt: RemoteDashReceipt): void {
    if (!receipt?.sessionId || !Number.isFinite(receipt.x) || !Number.isFinite(receipt.y)) return;
    if (!this.acceptSequence(receipt)) return;
    // Instant movement and abilities without art also replace the old flight.
    this.cancel(receipt.sessionId);
    if (!(receipt.travelMs > 0)) { this.deps.instant(receipt); return; }
    const geometry = resolveCastGeometry(receipt.abilityId)?.dash;
    const carries = geometry?.mode === 'leap' && geometry.horizontalPolicy === 'until-landed';
    const waitingForGroundContact = !carries && receipt.abilityId === 'lanternskip';
    const vfx = receipt.abilityId ? ABILITY_DATA[receipt.abilityId]?.vfx : undefined;
    // Authored trail/ghosts/origin/landing definitions win; legacy TOML serves as fallback.
    const trailId = geometry?.trailEffectId ?? (carries ? geometry?.originEffectId : undefined) ?? vfx?.castEffect;
    const landingId = (carries
      ? geometry?.landingEffectId
      : (geometry?.destinationEffectId ?? geometry?.landingEffectId)) ?? vfx?.impactEffect;
    const hasOrigin = Boolean(geometry?.originEffectId);
    const hasGhosts = Boolean(geometry?.ghosts);
    if ((!trailId && !landingId && !hasOrigin && !hasGhosts) || this.runs.size >= REMOTE_VFX_CONFIG.maxActiveRemoteCasts) return;

    let spawnedOrigin = false;
    const pose = this.deps.caster(receipt.sessionId);
    if (!carries && pose && geometry?.originEffectId) {
      const flipX = receipt.x !== pose.position.x ? receipt.x < pose.position.x : pose.facing === 'left';
      this.deps.spawn({
        effectId: geometry.originEffectId,
        ...playerCombatPoint(pose),
        abilityId: receipt.abilityId,
        duration: 300,
        flipX,
        follow: false,
      });
      spawnedOrigin = true;
    }

    const cadence = Math.max(
      TRAIL_FX_CONFIG.minGhostIntervalMs,
      geometry?.ghosts?.intervalMs ?? MOVEMENT_TRAVEL_CONFIG.trail.intervalMs
    );
    const run: Run = {
      receipt, geometry, startedAt: this.now(), carries, waitingForGroundContact, trailId, landingId,
      seenCaster: Boolean(pose), spawnedOrigin, airborne: false,
      interval: setInterval(() => this.tick(receipt.sessionId), cadence),
      timeout: setTimeout(() => {
        if (this.runs.get(receipt.sessionId) !== run) return;
        if (carries || run.waitingForGroundContact) this.cancel(receipt.sessionId); // timeout never invents a landing
        else this.finishTimed(run);
      }, (carries || waitingForGroundContact) ? REMOTE_DASH_MAX_MS : Math.min(receipt.travelMs, REMOTE_DASH_MAX_MS)),
    };
    this.runs.set(receipt.sessionId, run);
  }

  cancel(sessionId: string): void {
    const run = this.runs.get(sessionId);
    if (!run) return;
    clearInterval(run.interval); clearTimeout(run.timeout); this.runs.delete(sessionId);
  }

  remove(sessionId: string): void { this.cancel(sessionId); this.sequences.delete(sessionId); }
  clear(): void {
    for (const id of this.runs.keys()) this.cancel(id);
    this.sequences.clear();
  }
  activeCount(): number { return this.runs.size; }

  private now(): number { return this.deps.now?.() ?? Date.now(); }

  private acceptSequence(data: RemoteDashReceipt): boolean {
    const next = data.clientDashSeq;
    if (next === undefined) return true; // legacy receipts cannot distinguish recasts
    if (!Number.isInteger(next) || next < 1 || next > MAX_SEQUENCE) return false;
    const previous = this.sequences.get(data.sessionId);
    if (previous !== undefined) {
      const distance = (next - previous + MAX_SEQUENCE) % MAX_SEQUENCE;
      if (distance === 0 || distance > MAX_SEQUENCE / 2) return false;
    }
    this.sequences.delete(data.sessionId); this.sequences.set(data.sessionId, next);
    if (this.sequences.size > MAX_SEQUENCE_HISTORY) this.sequences.delete(this.sequences.keys().next().value!);
    return true;
  }

  private livePose(run: Run): RemoteDashPose | null {
    const pose = this.deps.caster(run.receipt.sessionId);
    if (!pose || pose.isOnline === false || pose.isDead || (pose.health !== undefined && pose.health <= 0)
      || !Number.isFinite(pose.position.x) || !Number.isFinite(pose.position.y)) {
      if (pose || run.seenCaster || this.now() - run.startedAt >= HYDRATION_GRACE_MS) this.cancel(run.receipt.sessionId);
      return null;
    }
    run.seenCaster = true;
    return pose;
  }

  private tick(sessionId: string): void {
    const run = this.runs.get(sessionId);
    if (!run) return;
    const pose = this.livePose(run);
    if (!pose) return;

    // Delayed hydration: spawn origin effect if it wasn't ready at start()
    if (!run.carries && !run.spawnedOrigin && run.geometry?.originEffectId) {
      const flipX = run.receipt.x !== pose.position.x ? run.receipt.x < pose.position.x : pose.facing === 'left';
      this.deps.spawn({
        effectId: run.geometry.originEffectId,
        ...playerCombatPoint(pose),
        abilityId: run.receipt.abilityId,
        duration: 300,
        flipX,
        follow: false,
      });
      run.spawnedOrigin = true;
    }

    if (run.carries) {
      const authority = pose.authority;
      // A cast receipt may precede its first position patch. Grounded at the
      // start is not a landing; require a witnessed rising/airborne phase.
      if (authority && (authority.velocityY < 0
        || (!authority.isGrounded && this.now() - run.startedAt >= LAUNCH_OBSERVATION_MS))) run.airborne = true;
      const grounded = authority?.isGrounded && authority.velocityY >= 0;
      if (run.airborne && grounded && this.now() - run.startedAt >= LAUNCH_OBSERVATION_MS
        && Math.abs(pose.position.y - authority.y) <= LANDING_POSE_EPSILON_PX) {
        this.cancel(sessionId);
        if (run.landingId) this.deps.spawn({ effectId: run.landingId, ...playerCombatPoint(pose),
          abilityId: run.receipt.abilityId, duration: 300, follow: false });
        return;
      }
    }

    if (run.waitingForGroundContact) {
      const elapsed = this.now() - run.startedAt;
      const authority = pose.authority;
      if (authority && (authority.velocityY < 0 || !authority.isGrounded)) run.airborne = true;
      const grounded = authority ? (authority.isGrounded && authority.velocityY >= 0) : Boolean(pose.isGrounded);
      const poseAgrees = !authority || Math.abs(pose.position.y - authority.y) <= LANDING_POSE_EPSILON_PX;
      if (elapsed >= run.receipt.travelMs && run.airborne && grounded && poseAgrees) {
        this.cancel(sessionId);
        if (run.landingId) {
          const flipX = pose.facing === 'left';
          this.deps.spawn({
            effectId: run.landingId,
            ...playerCombatPoint(pose),
            abilityId: run.receipt.abilityId,
            duration: 300,
            flipX,
            follow: false,
          });
        }
        this.deps.arrival(run.receipt);
        return;
      }
      if (elapsed >= run.receipt.travelMs) {
        // Horizontal travel completed, waiting for physical ground contact before burst
        return;
      }
    }

    const ghosts = run.geometry?.ghosts;
    const at = playerCombatPoint(pose);
    const textureKey = this.deps.textureKey(sessionId);
    if (textureKey && TrailSystem.spawnGhost({
      ...at,
      textureKey,
      abilityId: run.receipt.abilityId,
      ...(ghosts ? {
        tint: ghosts.tint,
        edgeTint: ghosts.edgeTint,
        lifeMs: ghosts.lifeMs,
        startAlpha: ghosts.startAlpha,
        blend: ghosts.blend,
        flipX: pose.facing === 'left',
      } : { flipX: pose.facing === 'left' }),
    })) return;
    if (run.trailId) {
      this.deps.spawn({
        effectId: run.trailId,
        ...at,
        abilityId: run.receipt.abilityId,
        duration: ghosts?.lifeMs ?? MOVEMENT_TRAVEL_CONFIG.trail.durationMs,
        follow: false,
      });
    }
  }

  private finishTimed(run: Run): void {
    const pose = this.livePose(run);
    this.cancel(run.receipt.sessionId);
    if (!pose) return;
    const area = resolveCastGeometry(run.receipt.abilityId)?.area;
    if (run.landingId && run.landingId !== area?.effectId) {
      const flipX = pose.facing === 'left';
      this.deps.spawn({
        effectId: run.landingId,
        ...playerCombatPoint({ x: run.receipt.x, y: run.receipt.y }),
        abilityId: run.receipt.abilityId,
        duration: 300,
        flipX,
        follow: false,
      });
    }
    this.deps.arrival(run.receipt);
  }
}
